import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { autopilotToday, autopilotSet, autopilotRetry } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Robot, CalendarBlank, ListChecks, Pause, Play, ArrowsClockwise, Sparkle,
  FacebookLogo, InstagramLogo, EnvelopeSimple, ChatText, Article } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLATFORM = {
  Facebook: { icon: FacebookLogo, cls: "text-sky-400", label: "Facebook" },
  Instagram: { icon: InstagramLogo, cls: "text-pink-400", label: "Instagram" },
  Email: { icon: EnvelopeSimple, cls: "text-emerald-400", label: "Email Campaign" },
  SMS: { icon: ChatText, cls: "text-violet-400", label: "SMS Reminder" },
  Blog: { icon: Article, cls: "text-amber-400", label: "Blog Article" },
};
const localTime = (dateStr, timeStr) => {
  const d = new Date(`${dateStr}T${timeStr}:00Z`);
  return isNaN(d) ? timeStr : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};
const statusOf = (it, now, autopilotActive) => {
  if (it.status === "published") return { e: "🟢", label: "Published", cls: "text-emerald-400" };
  if (it.status === "failed") return { e: "🔴", label: "Failed", cls: "text-red-400" };
  if (it.status === "pending") return { e: "🟡", label: "Waiting", cls: "text-amber-300" };
  if (it.status === "scheduled" || it.status === "approved") {
    const due = new Date(`${now.toISOString().slice(0, 10)}T${it.time}:00Z`);
    if (autopilotActive && due <= now) return { e: "🟡", label: "Publishing…", cls: "text-amber-300 animate-pulse" };
    return { e: "🟢", label: "Scheduled", cls: "text-sky-300" };
  }
  return { e: "⚪", label: it.status, cls: "text-[#9a9a9a]" };
};
const countdownTo = (dateStr, timeStr) => {
  const ms = new Date(`${dateStr}T${timeStr}:00Z`) - Date.now();
  if (ms <= 0) return "now";
  const mins = Math.floor(ms / 60000);
  const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function TodaysSchedule({ clientId, onItemClick }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 30000); return () => clearInterval(t); }, []);
  const { data } = useQuery({
    queryKey: ["autopilot-today", clientId],
    queryFn: () => autopilotToday(clientId),
    enabled: !!clientId, refetchInterval: 30000,
  });
  const setState = useMutation({
    mutationFn: (state) => autopilotSet(clientId, state),
    onSuccess: (_d, state) => { qc.invalidateQueries({ queryKey: ["autopilot-today", clientId] }); qc.invalidateQueries({ queryKey: ["autopilot-status", clientId] }); toast.success(state === "active" ? "Auto Pilot resumed" : "Auto Pilot paused"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update Auto Pilot"),
  });
  const retry = useMutation({
    mutationFn: (id) => autopilotRetry(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["autopilot-today", clientId] }); toast.success("Rescheduled — Auto Pilot will retry shortly"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Retry failed"),
  });
  if (!data) return <div className="card-matte p-6 mb-6 h-40 animate-pulse" data-testid="todays-schedule-skeleton" />;

  const now = new Date();
  const s = data.summary || {};
  const apActive = data.autopilot_state === "active";
  const items = data.items || [];

  return (
    <div className="card-matte p-5 md:p-6 mb-6 fade-up" data-testid="todays-schedule-widget">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#6B3DFF]/12 flex items-center justify-center">
            <Robot size={20} className="brand-text" weight="duotone" />
          </div>
          <div>
            <h2 className="font-head font-bold text-lg leading-tight">Today's AI Schedule</h2>
            <p className="text-xs text-[#9a9a9a]">Here's what your AI team is doing today.</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="todays-quick-actions">
          <Button data-testid="today-open-calendar" size="sm" variant="ghost" onClick={() => navigate("/scheduler")}
            className="h-8 rounded-full border border-white/10 text-[#cfcfcf] hover:text-white hover:bg-white/5 text-xs px-3">
            <CalendarBlank size={13} className="mr-1.5" /> Calendar
          </Button>
          <Button data-testid="today-open-queue" size="sm" variant="ghost" onClick={() => navigate("/approvals")}
            className="h-8 rounded-full border border-white/10 text-[#cfcfcf] hover:text-white hover:bg-white/5 text-xs px-3">
            <ListChecks size={13} className="mr-1.5" /> Approvals
          </Button>
          {apActive ? (
            <Button data-testid="today-pause-autopilot" size="sm" variant="ghost" onClick={() => setState.mutate("paused")}
              className="h-8 rounded-full border border-white/10 text-[#cfcfcf] hover:text-white hover:bg-white/5 text-xs px-3">
              <Pause size={13} className="mr-1.5" weight="fill" /> Pause Auto Pilot
            </Button>
          ) : data.autopilot_state === "paused" ? (
            <Button data-testid="today-resume-autopilot" size="sm" variant="ghost" onClick={() => setState.mutate("active")}
              className="h-8 rounded-full border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs px-3">
              <Play size={13} className="mr-1.5" weight="fill" /> Resume Auto Pilot
            </Button>
          ) : null}
        </div>
      </div>

      {items.length > 0 && !apActive && items.some((i) => i.status === "scheduled") && (
        <div className="mb-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-2.5 flex items-center gap-2.5" data-testid="autopilot-off-warning">
          <span className="text-amber-300 text-xs flex-1">
            ⚠️ Auto Pilot is {data.autopilot_state === "paused" ? "paused" : "off"} — scheduled items will <b>not</b> publish automatically.
          </span>
          <Button data-testid="today-warning-enable" size="sm" onClick={() => setState.mutate("active")}
            className="h-7 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs px-3 shrink-0">
            <Play size={12} className="mr-1" weight="fill" /> Turn on
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-8" data-testid="todays-empty-state">
          <div className="text-4xl mb-3">🎉</div>
          <div className="font-head font-semibold">Your AI team has no scheduled tasks today.</div>
          <div className="mt-4 flex justify-center gap-3">
            <Button data-testid="today-generate-campaign" onClick={() => navigate("/monthly")}
              className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5 text-sm">
              <Sparkle size={14} className="mr-2" weight="fill" /> Generate Monthly Campaign
            </Button>
            <Button data-testid="today-empty-calendar" variant="ghost" onClick={() => navigate("/scheduler")}
              className="rounded-full border border-white/10 text-white hover:bg-white/5 h-10 px-5 text-sm">
              Open Marketing Calendar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="todays-schedule-list">
          {items.map((it, k) => {
            const P = PLATFORM[it.platform] || PLATFORM.Blog;
            const st = statusOf(it, now, apActive);
            return (
              <div key={it.id} data-testid={`today-item-${it.id}`}
                onClick={() => onItemClick && onItemClick(it.id)}
                className={`flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-3.5 py-2.5 transition-colors hover:border-white/15 ${onItemClick ? "cursor-pointer" : ""}`}
                style={{ animation: `demo-fade .4s ease-out ${k * 0.06}s both` }}>
                <span className={`text-sm ${st.cls} shrink-0 w-24 font-semibold`} data-testid={`today-status-${it.id}`}>{st.e} {st.label}</span>
                <span className="text-sm font-mono text-white w-20 shrink-0">{localTime(data.date, it.time)}</span>
                <P.icon size={16} className={`${P.cls} shrink-0`} weight="fill" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#e6e6e6] truncate">{it.title}</div>
                  <div className="text-[10px] text-[#7a7a7a]">{P.label} · {it.created_by || "Vozelto AI Team"}</div>
                  {it.blocked_reason && <div className="text-[10px] text-amber-300 mt-0.5" data-testid={`today-blocked-${it.id}`}>⚠️ {it.blocked_reason}</div>}
                  {it.status === "failed" && it.error && <div className="text-[10px] text-red-300 mt-0.5 truncate" data-testid={`today-error-${it.id}`}>{it.error}</div>}
                </div>
                {it.status === "failed" && (
                  <Button data-testid={`today-retry-${it.id}`} size="sm" onClick={(ev) => { ev.stopPropagation(); retry.mutate(it.id); }} disabled={retry.isPending}
                    className="h-7 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs px-3 shrink-0">
                    <ArrowsClockwise size={12} className="mr-1" /> Retry
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="todays-summary">
        {[
          ["Scheduled Today", s.scheduled_today ?? 0],
          ["Published", s.published_today ?? 0],
          ["Waiting Approval", s.waiting_approval ?? 0],
          ["Failed", s.failed_today ?? 0, (s.failed_today || 0) > 0 ? "text-red-400" : ""],
          ["Success Rate", s.success_rate == null ? "No data yet" : `${s.success_rate}%`, s.success_rate == null ? "text-[#7a7a7a]" : (s.failed_today || 0) > 0 ? "text-amber-300" : "text-emerald-400"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2">
            <div className="label-caps text-[8px]">{label}</div>
            <div className={`text-sm font-bold font-head ${cls || "text-white"}`}>{val}</div>
          </div>
        ))}
        <div className="rounded-lg bg-white/[0.03] border border-white/8 px-3 py-2" data-testid="todays-next-publish">
          <div className="label-caps text-[8px]">Next Publish</div>
          {s.next_publish ? (
            <div className="text-sm font-bold font-head text-white">
              {localTime(s.next_publish.date, s.next_publish.time)}
              <span className="ml-1.5 text-[10px] brand-text font-semibold">in {countdownTo(s.next_publish.date, s.next_publish.time)}</span>
            </div>
          ) : <div className="text-sm font-bold font-head text-[#7a7a7a]">—</div>}
        </div>
      </div>
    </div>
  );
}
